import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const PAYMENT_MODELS = ["monthly", "per_course", "per_lesson_package"] as const;

export default function Institution() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canEdit = hasPerm("institution.edit");
    const [state, setState] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    // Phase PM-1: 409 payload from the in-use blocker. When present we
    // render a dedicated card instead of the generic error banner so the
    // operator immediately sees *which* programs still hold the model.
    const [inUse, setInUse] = useState<null | {
        removedModel: string;
        count: number;
        sample: { id: string; name: string }[];
    }>(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/institution");
            setState(data);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const togglePaymentModel = (m: string) => {
        if (!state) return;
        const cur: string[] = state.payment_models || [];
        const next = cur.includes(m) ? cur.filter((x: string) => x !== m) : [...cur, m];
        setState({ ...state, payment_models: next });
    };

    const save = async () => {
        if (!state) return;
        const models: string[] = state.payment_models || [];
        if (models.length === 0) {
            setErr(t("institution.payment_models.min_one")); return;
        }
        setSaving(true); setErr(null); setInUse(null);
        try {
            const { data } = await api.patch("/institution", {
                name: state.name,
                legal_name: state.legal_name,
                tax_id: state.tax_id,
                contact_email: state.contact_email,
                contact_phone: state.contact_phone,
                country: state.country,
                language: state.language,
                currency: state.currency,
                timezone: state.timezone,
                date_format: state.date_format,
                payment_models: models,
            });
            setState(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e: any) {
            // Detect the structured 409 payload from Phase PM-1.
            const detail = e?.response?.data?.detail;
            if (detail && detail.error === "payment_model_in_use" && detail.usage) {
                const firstModel = Object.keys(detail.usage)[0];
                const u = detail.usage[firstModel];
                setInUse({ removedModel: firstModel, count: u.count, sample: u.sample || [] });
                // Restore the model that couldn't be removed so the UI matches server truth.
                const restored = Array.from(new Set([...(state.payment_models || []), firstModel]));
                setState({ ...state, payment_models: restored });
            } else {
                setErr(formatApiError(e));
            }
        } finally { setSaving(false); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={22} /></div>;
    if (!state) {
        return (
            <div className="space-y-6 max-w-3xl" data-testid="institution-page">
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("institution.title")}</h1>
                <div className="surface-card p-6 flex items-start gap-3" data-testid="institution-denied" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>
                    <span className="text-sm">{err || t("common.no_permission")}</span>
                </div>
            </div>
        );
    }

    const activeModels: string[] = state.payment_models || [];

    return (
        <div className="space-y-6 max-w-3xl" data-testid="institution-page">
            <div>
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("institution.title")}</h1>
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {inUse && (
                <div className="rounded-md p-4 text-sm space-y-2" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="payment-model-in-use">
                    <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={16} />{t("institution.payment_models.in_use_title", { model: t(`teaching.${inUse.removedModel}`) })}</div>
                    <p>{t("institution.payment_models.in_use_desc", { count: inUse.count })}</p>
                    <div>
                        <div className="text-xs font-medium mb-1">{t("institution.payment_models.in_use_sample")}</div>
                        <ul className="list-disc ml-5 text-xs">
                            {inUse.sample.map(s => <li key={s.id} data-testid={`in-use-sample-${s.id}`}>{s.name}</li>)}
                        </ul>
                    </div>
                </div>
            )}
            {saved && <div className="rounded-md p-3 text-sm flex items-center gap-2" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }} data-testid="institution-saved"><CheckCircle2 size={16} /> {t("institution.payment_models.saved")}</div>}
            <div className="surface-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("onboarding.institution_name")}><input data-testid="inst-name" disabled={!canEdit} value={state.name || ""} onChange={(e) => setState({ ...state, name: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("institution.legal_name")}><input data-testid="inst-legal" disabled={!canEdit} value={state.legal_name || ""} onChange={(e) => setState({ ...state, legal_name: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("institution.tax_id")}><input data-testid="inst-tax" disabled={!canEdit} value={state.tax_id || ""} onChange={(e) => setState({ ...state, tax_id: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("institution.contact_email")}><input data-testid="inst-email" type="email" disabled={!canEdit} value={state.contact_email || ""} onChange={(e) => setState({ ...state, contact_email: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("institution.contact_phone")}><input data-testid="inst-phone" disabled={!canEdit} value={state.contact_phone || ""} onChange={(e) => setState({ ...state, contact_phone: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("onboarding.country")}><input data-testid="inst-country" disabled={!canEdit} value={state.country || ""} onChange={(e) => setState({ ...state, country: e.target.value.toUpperCase().slice(0, 2) })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("onboarding.language")}><input data-testid="inst-lang" disabled={!canEdit} value={state.language || ""} onChange={(e) => setState({ ...state, language: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("onboarding.currency")}><input data-testid="inst-currency" disabled={!canEdit} value={state.currency || ""} onChange={(e) => setState({ ...state, currency: e.target.value.toUpperCase() })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("onboarding.timezone")}><input data-testid="inst-tz" disabled={!canEdit} value={state.timezone || ""} onChange={(e) => setState({ ...state, timezone: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                <Field label={t("institution.date_format")}><input data-testid="inst-df" disabled={!canEdit} value={state.date_format || ""} onChange={(e) => setState({ ...state, date_format: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
            </div>

            {/* Phase PM-1: multi-select payment models */}
            <div className="surface-card p-5 space-y-3" data-testid="payment-models-section">
                <div>
                    <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{t("institution.payment_models.title")}</h2>
                    <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t("institution.payment_models.desc")}</p>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                    {PAYMENT_MODELS.map((m: string) => {
                        const on = activeModels.includes(m);
                        return (
                            <button key={m} type="button" data-testid={`payment-model-toggle-${m}`}
                                onClick={() => canEdit && togglePaymentModel(m)}
                                disabled={!canEdit}
                                className="text-left rounded-md p-4 border transition-colors disabled:opacity-70"
                                style={{
                                    background: on ? "var(--selected-bg)" : "var(--input)",
                                    borderColor: on ? "var(--primary)" : "var(--border)",
                                    minHeight: 44,
                                }}>
                                <div className="flex items-start gap-2">
                                    <span className="rounded-md inline-flex items-center justify-center shrink-0" style={{ width: 20, height: 20, background: on ? "var(--primary)" : "var(--card)", color: on ? "var(--primary-fg)" : "var(--text-muted)", border: `1px solid ${on ? "var(--primary)" : "var(--border)"}` }}>
                                        {on ? <CheckCircle2 size={14} /> : null}
                                    </span>
                                    <div>
                                        <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t(`teaching.${m}`)}</div>
                                        <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t(`teaching.desc.${m}`)}</div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                {activeModels.length === 0 && (
                    <p className="text-xs" style={{ color: "var(--warning-fg)" }} data-testid="payment-models-min-warn">{t("institution.payment_models.min_one")}</p>
                )}
            </div>

            {canEdit && (
                <button data-testid="inst-save-btn" onClick={save} disabled={saving || activeModels.length === 0} className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                    {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {t("common.save")}
                </button>
            )}
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>{children}</label>;
}
